import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {
    SelectInput,
    TextInput
} from 'admin-on-rest';
import DateRangeFields from './DateRangeFields';
import {TIME_ZONE_OFFSETS} from '../shared/dateTimeLists';
import {commonStyles} from '../shared/styles';
import {filterStyles} from '../shared/styles-filter';

/**
 * Date Range Type selection for Schedule Report form
 *
 */
class ScheduleReportFormDateRangeTypeFields extends Component {
    constructor(props) {
        super(props);
        const currentYear = new Date().getFullYear();
        const startMinDate = new Date();
        startMinDate.setFullYear(currentYear - 1);
        this.state = {
            backNum: 1,
            backType: null,
            dateRangeType: null,
            startMinDate: startMinDate
        };
        this.handleDateSelect = this.handleDateSelect.bind(this);
        this.handleFieldUpdate = this.handleFieldUpdate.bind(this);
    }

    // Notify form of Start/End/Offset selection
    handleDateSelect(field, value) {
        if (field && value && typeof this.props.onFieldUpdate === 'function') {
            this.props.onFieldUpdate(field, value);
        }
    }

    // Store and notify form of field updates
    handleFieldUpdate(field, event, index) {
        let fieldValue = (index) ? index : null;

        if (fieldValue) {
            // Display Date Range selection
            if (field === 'dateRangeType') {
                this.setState({
                    dateRangeType: fieldValue
                });
            }
            if (field === 'backNum' || field === 'backType') {
                // Limit Days back for Relative range
                this.limitRange(field, fieldValue);
            } else if (typeof this.props.onFieldUpdate === 'function') {
                // Otherwise, notify form
                this.props.onFieldUpdate(field, fieldValue);
            }
        } else {
            if (field === 'backNum') {
                // Allow empty field for Days Back
                this.setState({
                    backNum: ''
                }, () => {
                    if (typeof this.props.onFieldUpdate === 'function') {
                        this.props.onFieldUpdate('backNum', '');
                    }
                });
            }
        }
    }

    // Limit Days back to 1 year for Relative Range Type
    limitRange = (field, value) => {
        let backNum = (field === 'backNum') ? parseInt(value, 10) : parseInt(this.state.backNum, 10);
        let backType = (field === 'backType') ? value : this.state.backType;
        if (backNum) {
            backNum = Math.trunc(backNum);
        }
        let newBackNum = -1;
        if (backType === 'months' && backNum > 12) {
            newBackNum = 12;
        }
        if (backType === 'weeks' && backNum > 52) {
            newBackNum = 52;
        }
        if ((backType === 'days' || !backType) && backNum > 365) {
            newBackNum = 365;
        }
        this.setState({
            backNum: (newBackNum > 0) ? newBackNum : backNum,
            backType: backType
        }, () => {
            if (typeof this.props.onFieldUpdate === 'function') {
                this.props.onFieldUpdate('backNum', (newBackNum > 0) ? newBackNum : backNum);
                this.props.onFieldUpdate('backType', backType);
            }
        });
    };

    render() {
        const {
            backNum,
            dateRangeType,
            startMinDate
        } = this.state;

        const {
            formValues
        } = this.props;

        return (
            <Fragment>
                <hr style={{...commonStyles.divider, ...commonStyles.mt1, ...commonStyles.mb1}}/>
                <h3>Report Date Range:</h3>
                <p>
                    Select a range type: a specific range of dates, or a date relative to the
                    Report's run time.
                </p>
                <ul style={commonStyles.mb0}>
                    <li>
                        <strong>Specific Start/End Dates</strong> use the same Start Date and End Date
                        each time the Report runs. If the Report runs before the End Date, it will
                        encompass data from the Start Date to report runtime.
                        <br/><br/>
                    </li>
                    <li>
                        <strong>Relative to Report Runtime</strong> covers a span of time back from
                        each time the Report runs. The report will include data from the End Date
                        to a set amount of days, weeks or months back, to a maximum of one year.
                    </li>
                </ul>

                <Field
                    name="dateRangeType"
                    component={SelectInput}
                    label="Date Range Type"
                    choices={[
                        {id: 'specific', name: 'Specific Start/End Dates'},
                        {id: 'relative', name: 'Relative to Report Runtime'}
                    ]}
                    onChange={(e, i, v) => this.handleFieldUpdate('dateRangeType', e, i, v)}
                    elStyle={{...filterStyles.selectInput, ...filterStyles.selectInputWide}}
                />

                {dateRangeType === 'specific' &&
                    <Fragment>
                        <p style={commonStyles.mb0}>
                            Select specific Start and End dates and a time zone offset.
                        </p>
                        <DateRangeFields
                            labelStartDate="Set First Date in Range:"
                            labelEndDate="Set Last Date in Range:"
                            labelOffset="Set Time Zone Offset:"
                            startMinDate={startMinDate}
                            onSelectStartDate={this.handleDateSelect}
                            onSelectEndDate={this.handleDateSelect}
                            onSelectOffset={this.handleDateSelect}
                        />
                    </Fragment>
                }

                {dateRangeType === 'relative' &&
                    <Fragment>
                        <p style={commonStyles.mb0}>
                            Select a span of time before the Report's runtime and a time zone offset.
                            Maximum time is 1 year.
                        </p>
                        <div className="list-filter">
                            <span style={filterStyles.inlineText}>Report Date to:</span>
                            <Field
                                name="backNum"
                                component={TextInput}
                                label="Number"
                                value={backNum}
                                options={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    step: 1,
                                    min: 1,
                                    max: 365,
                                    value: backNum
                                }}
                                onChange={(e, i, v) => this.handleFieldUpdate('backNum', e, i, v)}
                                elStyle={{...filterStyles.inline, ...filterStyles.selectInputSmall}}
                            />
                            <Field
                                name="backType"
                                label="of Type"
                                placeholder="Days Before"
                                component={SelectInput}
                                choices={[
                                    {id: 'days', name: 'Days Before'},
                                    {id: 'weeks', name: 'Weeks Before'},
                                    {id: 'months', name: 'Months Before'},
                                ]}
                                value={formValues.backType}
                                onChange={(e, i, v) => this.handleFieldUpdate('backType', e, i, v)}
                                elStyle={{...filterStyles.selectInput, ...filterStyles.inline}}
                            />
                            <Field
                                name="offset"
                                component={SelectInput}
                                label="Select a Time Zone Offset"
                                choices={TIME_ZONE_OFFSETS}
                                onChange={(e, i, v) => this.handleFieldUpdate('offset', e, i, v)}
                                elStyle={filterStyles.selectInput}
                            />
                        </div>
                    </Fragment>
                }
            </Fragment>
        );
    }
}

ScheduleReportFormDateRangeTypeFields.propTypes = {
    formValues: PropTypes.object,
    onFieldUpdate: PropTypes.func
};

export default ScheduleReportFormDateRangeTypeFields;



// WEBPACK FOOTER //
// ./src/Fuse/components/ScheduleReportFormDateRangeTypeFields.js